import './edit-user.scss'

import clsx from 'clsx'

import { UserInformationBasic } from '@/api'

import { Space, ValidationMessage } from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import { UserType } from '../../../types/user'
import { useClientsLayout } from '../clients-layout-provider'
import { isErrorWith } from '../common'
import { Mutation } from '../types'
import { RoleDfmAndCatSection } from './role-dfm-and-cat-section'
import { RoleSection } from './role-section'
import { useEditMachine } from './use-edit-machine'

export type EditUserStatelessProps = {
  errors: { [key: string]: string }
  initialUser?: UserInformationBasic
  isSaveEnabled: boolean
  onMutate: (mutation: Mutation<UserInformationBasic>) => void
  onSave: (user: UserInformationBasic, initialUser: UserInformationBasic) => void
  user?: UserInformationBasic
  userType: UserType
}

export function EditUserStateless({
  errors,
  initialUser,
  isSaveEnabled,
  onMutate,
  onSave,
  user,
  userType
}: EditUserStatelessProps) {
  const { isPortfolioManagerScreen } = useClientsLayout()

  return (
    <div className="edit-user">
      <div className="block">
        <label
          className={clsx({
            error: isErrorWith(errors, 'firstName')
          })}
          htmlFor="edit-user__first-name"
        >
          First name
        </label>
        <input
          className={clsx({
            error: isErrorWith(errors, 'firstName')
          })}
          id="edit-user__first-name"
          value={user.firstName}
          onChange={v => onMutate(u => (u.firstName = v.target.value))}
        />
        <ValidationMessage errors={errors} fieldName="firstName" />
      </div>
      <div className="block">
        <label
          className={clsx({
            error: isErrorWith(errors, 'lastName')
          })}
          htmlFor="edit-user__last-name"
        >
          Last name
        </label>
        <input
          className={clsx({
            error: isErrorWith(errors, 'lastName')
          })}
          id="edit-user__last-name"
          value={user.lastName}
          onChange={v => onMutate(u => (u.lastName = v.target.value))}
        />
        <ValidationMessage errors={errors} fieldName="lastName" />
      </div>
      {userType === 'business-unit' && !isPortfolioManagerScreen && (
        <RoleSection user={user} onMutate={onMutate} />
      )}
      {(userType === 'business-unit' || isPortfolioManagerScreen) && (
        <RoleDfmAndCatSection
          errors={errors}
          isPortfolioManagerScreen={isPortfolioManagerScreen}
          user={user}
          onMutate={onMutate}
        />
      )}
      <div className="block">
        <label htmlFor="edit-user__username">Username</label>
        <input disabled id="edit-user__username" value={user.userName} />
      </div>
      <div className="block">
        <label htmlFor="edit-user__email">Email</label>
        <input disabled id="edit-user__email" value={user.email} />
      </div>
      <Space mt={5}>
        <IconButton
          data-testid="update-user"
          disabled={!isSaveEnabled}
          icon={<Icon name="save" size="default" />}
          iconPosition="left"
          operationType="progress"
          text="Update user"
          onClick={() => onSave(user, initialUser)}
        />
      </Space>
    </div>
  )
}

type Props = {
  notifyParentOfSave: () => void
  user: UserInformationBasic
  userType: UserType
}

export function EditUser({ notifyParentOfSave, user, userType }: Props) {
  const { machine, send } = useEditMachine({ notifyParentOfSave, user })

  return (
    <EditUserStateless
      errors={machine.context.errors}
      initialUser={machine.context.initialUser}
      isSaveEnabled={machine.context.isSaveEnabled}
      user={machine.context.user}
      userType={userType}
      onMutate={mutation => send({ type: 'MUTATE', mutation })}
      onSave={(user, initialUser) =>
        send({ type: 'SAVE', user: user as any, initialUser: initialUser as any })
      }
    />
  )
}
